import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStats, getActivity, getSites, pingAll } from '../api';
import { Globe, Puzzle, Palette, RefreshCw, Zap, AlertTriangle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [activity, setActivity] = useState([]);
  const [pinging, setPinging] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getStats().then(setStats).catch(console.error);
    getSites().then(setSites).catch(console.error);
    getActivity(10).then(setActivity).catch(console.error);
  }, []);

  const handlePingAll = async () => {
    setPinging(true);
    try {
      await pingAll();
      const s = await getSites();
      setSites(s);
      toast.success('All sites checked!');
    } finally { setPinging(false); }
  };

  const statusColor = s => s === 'online' ? 'var(--green)' : s === 'offline' ? 'var(--red)' : 'var(--amber)';
  const resultBadge = r => r === 'success' ? 'badge green' : r === 'error' ? 'badge red' : 'badge amber';

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>All your WordPress sites in one place</p>
        </div>
        <div className="header-actions">
          <Button className="btn" onClick={handlePingAll} loading={pinging}>
            <RefreshCw size={13} /> {pinging ? 'Checking...' : 'Check all sites'}
          </Button>
          <Button className="btn primary" onClick={() => navigate('/plugins')}>
            <Zap size={13} /> Update all
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-top">
              <div className="stat-icon"><Globe size={19} /></div>
            </div>
            <div className="stat-label">Total Sites</div>
            <div className="stat-val blue">{stats?.sites?.total ?? '—'}</div>
            <div className="stat-sub"><span className="dot on"></span> {stats?.sites?.online ?? 0} online</div>
          </div>
          <div className="stat-card">
            <div className="stat-top">
              <div className="stat-icon amber"><Puzzle size={19} /></div>
            </div>
            <div className="stat-label">Plugin Updates</div>
            <div className="stat-val amber">{stats?.plugins?.updates ?? '—'}</div>
            <div className="stat-sub"><Puzzle size={11} /> Across all sites</div>
          </div>
          <div className="stat-card">
            <div className="stat-top">
              <div className="stat-icon amber"><Palette size={19} /></div>
            </div>
            <div className="stat-label">Theme Updates</div>
            <div className="stat-val amber">{stats?.themes?.updates ?? '—'}</div>
            <div className="stat-sub"><Palette size={11} /> Across all sites</div>
          </div>
          <div className="stat-card">
            <div className="stat-top">
              <div className="stat-icon green"><Puzzle size={19} /></div>
            </div>
            <div className="stat-label">Total Plugins</div>
            <div className="stat-val">{stats?.plugins?.total ?? '—'}</div>
            <div className="stat-sub"><Puzzle size={11} /> Installed</div>
          </div>
        </div>

        <div className="two-col">
          {/* Sites overview */}
          <div className="card">
            <div className="card-head">
              <h3><Globe size={14} /> Sites overview</h3>
              <button className="btn sm" onClick={() => navigate('/sites')}>Manage →</button>
            </div>
            {sites.length === 0 ? (
              <div style={{ padding:'20px 16px', textAlign:'center', color:'var(--text3)', fontSize:12 }}>
                No sites yet. <span style={{ color:'var(--accent-text)', cursor:'pointer' }} onClick={() => navigate('/sites')}>Add your first site →</span>
              </div>
            ) : (
              <table className="tbl">
                <thead><tr><th>Site</th><th>Status</th><th>WP</th><th>Updates</th></tr></thead>
                <tbody>
                  {sites.map(s => (
                    <tr key={s.id}>
                      <td><span className="bold">{s.name}</span></td>
                      <td><span className="dot" style={{ background: statusColor(s.status) }}></span> {s.status}</td>
                      <td>{s.wp_version || '—'}</td>
                      <td>—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Activity */}
          <div className="card">
            <div className="card-head">
              <h3><Clock size={14} /> Recent activity</h3>
              <button className="btn sm" onClick={() => navigate('/activity')}>View all →</button>
            </div>
            {activity.length === 0 ? (
              <div style={{ padding:'20px 16px', textAlign:'center', color:'var(--text3)', fontSize:12 }}>No activity yet</div>
            ) : (
              <div style={{ padding:'8px 0' }}>
                {activity.map(a => (
                  <div key={a.id} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'7px 16px', borderBottom:'1px solid var(--border)' }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background: a.result==='success' ? 'var(--green)' : 'var(--red)', marginTop:5, flexShrink:0 }}></div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, color:'var(--text2)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{a.action.replace(/_/g,' ')}: {a.details}</div>
                      <div style={{ fontSize:10, color:'var(--text3)', marginTop:2 }}>{a.site_name} · {new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <span className={resultBadge(a.result)}>{a.result}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Attention needed */}
        {(stats?.plugins?.updates > 0 || stats?.themes?.updates > 0) && (
          <div className="card" style={{ borderColor:'var(--amber)', background:'var(--amber-bg)' }}>
            <div style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:10 }}>
              <AlertTriangle size={16} color="var(--amber)" />
              <div style={{ flex:1, fontSize:12, color:'var(--amber-text)' }}>
                <strong>{(stats?.plugins?.updates || 0) + (stats?.themes?.updates || 0)} updates</strong> pending across your sites.
              </div>
              <button className="btn amber" onClick={() => navigate('/plugins')}>Update plugins</button>
              <button className="btn amber" onClick={() => navigate('/themes')}>Update themes</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
